/**
 * Seller — "Tomar pedido" page.
 *
 * Lightweight, mobile-first order capture. Filters to ONLY the customers
 * assigned to the seller; if they type a name not in their portfolio, a
 * "+ Crear nuevo cliente" inline action lets them open a quick-create dialog
 * and the new customer is auto-assigned to them.
 *
 * After saving, opens WhatsApp share with the formatted order.
 */
import { useEffect, useMemo, useState } from "react";
import { api } from "../../App";
import { toast } from "sonner";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Autocomplete } from "../../components/Autocomplete";
import { POSProductGrid, POSCart } from "../../components/pos/POSBuilder";
import { formatMoney } from "../../lib/money";
import { buildPromoLines } from "../../lib/promociones";
import { Trash2, Send, MessageCircle, Plus, UserPlus, RotateCcw, RefreshCw } from "lucide-react";

// Auto-save draft to localStorage so the seller never loses work even if
// the browser tab dies or they accidentally navigate away.
const DRAFT_KEY = "mn_seller_order_draft_v1";
function loadDraft() {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const draft = JSON.parse(raw);
    // Discard drafts older than 24h to avoid stale data lingering forever.
    if (!draft || (Date.now() - (draft._ts || 0)) > 24 * 60 * 60 * 1000) return null;
    return draft;
  } catch { return null; }
}
function saveDraft(state) {
  try { localStorage.setItem(DRAFT_KEY, JSON.stringify({ ...state, _ts: Date.now() })); } catch { /* ignore */ }
}
function clearDraft() {
  try { localStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ }
}

// Quick-quantity stepper removed — replaced by shared POSCart component
// (see /components/pos/POSBuilder.jsx). The cart now exposes -, +, +1, +5,
// +10 controls per line which covers more granular bulk-add scenarios.

export default function SellerOrderTake() {
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cliente, setCliente] = useState("");
  const [clienteSel, setClienteSel] = useState(null); // full customer object when picked
  const [lineas, setLineas] = useState([]);
  const [saving, setSaving] = useState(false);
  const [lastSavedOrder, setLastSavedOrder] = useState(null);
  const [repeating, setRepeating] = useState(false);
  const [draftRestored, setDraftRestored] = useState(false);

  // New customer quick-create dialog — full admin-parity fields.
  const [newCustOpen, setNewCustOpen] = useState(false);
  const newCustEmpty = {
    nombre_facturacion: "",
    nombre_fantasia: "",
    cedula: "",
    phone: "",
    phone2: "",
    email: "",
    provincia: "",
    canton: "",
    distrito: "",
    codigo_postal: "",
    address: "",
    category: "",
    actividad_economica: "",
    maps_link: "",
    notes: "",
  };
  const [newCust, setNewCust] = useState(newCustEmpty);
  const [creatingCust, setCreatingCust] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [c, p] = await Promise.all([
          api.get("/seller/customers"),
          api.get("/seller/products"),
        ]);
        setCustomers(c.data);
        setProducts(p.data);
        // Restore draft once data is loaded so we can show the customer name
        const draft = loadDraft();
        if (draft && (draft.cliente || draft.lineas?.length)) {
          setCliente(draft.cliente || "");
          setClienteSel(draft.clienteSel || null);
          setLineas(draft.lineas || []);
          setDraftRestored(true);
          toast.info("Restauramos tu pedido en borrador");
        }
      } catch {
        toast.error("Error al cargar tus datos");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Auto-save the in-progress order to localStorage on every meaningful change.
  // We only persist when there's something to save AND we're not in the
  // middle of clearing after a save (lineas=[] && cliente='' → drop draft).
  useEffect(() => {
    if (loading) return;
    if (!cliente && lineas.length === 0) {
      clearDraft();
      return;
    }
    saveDraft({ cliente, clienteSel, lineas });
  }, [cliente, clienteSel, lineas, loading]);

  // Filtered products handled internally by <POSProductGrid />.

  const repeatLastOrder = async () => {
    if (!clienteSel) {
      toast.error("Seleccioná un cliente primero");
      return;
    }
    setRepeating(true);
    try {
      const { data } = await api.get(`/seller/customers/${clienteSel.id}/last-order`);
      if (!data?.order) {
        toast.info("Este cliente todavía no tiene pedidos previos");
        return;
      }
      const prev = data.order;
      // Replay lines respecting promo rules from the current catalog
      const replayed = [];
      for (const l of (prev.lineas || [])) {
        if (l.es_bonificacion) continue; // skip bonus lines — they get rebuilt
        const prod = products.find(p => (p.codigo || "").toLowerCase() === (l.codigo || "").toLowerCase());
        if (!prod) continue;
        const promo = buildPromoLines(prod, l.cantidad || 1);
        replayed.push(...promo);
      }
      if (replayed.length === 0) {
        toast.error("No se pudo replicar (productos no disponibles en el catálogo)");
        return;
      }
      setLineas(replayed);
      toast.success(`Replicamos ${replayed.length} línea${replayed.length === 1 ? "" : "s"} del pedido anterior`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo cargar el último pedido");
    } finally {
      setRepeating(false);
    }
  };

  // One-click add-from-catalog: when the user taps a product card, immediately
  // append a default-1 quantity line. If the line for that code already exists,
  // increment its quantity rather than creating a duplicate.
  const quickAddFromCatalog = (p, qty = 1) => {
    setLineas(prev => {
      const idx = prev.findIndex(l => (l.codigo || "").toLowerCase() === (p.codigo || "").toLowerCase() && !l.es_bonificacion);
      if (idx >= 0) {
        const next = [...prev];
        const current = next[idx];
        const newQty = (current.cantidad || 0) + qty;
        // Rebuild promo lines for that product with the new total qty
        const filtered = next.filter(l =>
          (l.codigo || "").toLowerCase() !== (p.codigo || "").toLowerCase()
        );
        const rebuilt = buildPromoLines(p, newQty);
        return [...filtered, ...rebuilt];
      }
      const promo = buildPromoLines(p, qty);
      return [...prev, ...promo];
    });
  };

  const total = useMemo(
    () => lineas.reduce((sum, l) => sum + (l.subtotal || 0), 0),
    [lineas],
  );

  // Match against the assigned-customer list (case-insensitive). If the user
  // types something that doesn't match any current customer, we offer the
  // "+ Crear nuevo cliente" inline action right under the field.
  const clienteTrimmed = (cliente || "").trim();
  const clienteMatchesExisting = useMemo(() => {
    if (!clienteTrimmed) return true;
    const q = clienteTrimmed.toLowerCase();
    return customers.some((c) => (c.nombre_facturacion || "").toLowerCase() === q);
  }, [customers, clienteTrimmed]);
  const showCreateNewHint = clienteTrimmed.length >= 3 && !clienteMatchesExisting;

  // Keep `clienteSel` in sync with the input: clear the snapshot when the
  // typed name no longer matches the picked customer.
  useEffect(() => {
    if (!clienteSel) return;
    if ((clienteSel.nombre_facturacion || "").trim().toLowerCase() !== clienteTrimmed.toLowerCase()) {
      setClienteSel(null);
    }
  }, [clienteTrimmed, clienteSel]);

  const pickCustomer = (c) => {
    setCliente(c.nombre_facturacion);
    setClienteSel(c);
  };

  const removeLine = (idx) => setLineas(lineas.filter((_, i) => i !== idx));

  // Re-apply discount + bonification when the qty of a regular line changes.
  // Removes any adjacent bonus line (es_bonificacion=true) tied to the same
  // codigo, then rebuilds via buildPromoLines so the cart stays in sync.
  const recalcLineWithPromo = (lineas, idx, newQty) => {
    const target = lineas[idx];
    if (!target) return lineas;
    const prod = products.find((x) => (x.codigo || "").toLowerCase() === (target.codigo || "").toLowerCase());
    // Drop the target + any bonus line immediately after for the same codigo
    const cleaned = lineas.filter((l, i) => {
      if (i === idx) return false;
      if (i === idx + 1 && l.es_bonificacion && l.codigo === target.codigo) return false;
      return true;
    });
    const replacement = prod
      ? buildPromoLines(prod, newQty)
      : [{ ...target, cantidad: newQty, subtotal: (target.precio_unitario || 0) * newQty }];
    // Splice replacement back at the original index
    const adjustedIdx = Math.min(idx, cleaned.length);
    return [
      ...cleaned.slice(0, adjustedIdx),
      ...replacement,
      ...cleaned.slice(adjustedIdx),
    ];
  };

  const editLineQty = (idx, delta) => {
    setLineas((prev) => {
      const line = prev[idx];
      if (!line || line.es_bonificacion) return prev;
      const newQty = Math.max(1, (line.cantidad || 1) + delta);
      return recalcLineWithPromo(prev, idx, newQty);
    });
  };
  // Note: editLineQty kept for backward compatibility (used by callers
  // outside this file in tests). The POSCart now drives qty changes via
  // setLineQty / setLinePrice directly.
  void editLineQty;

  const setLineQty = (idx, qty) => {
    const cantidad = Math.max(1, parseInt(qty || 0, 10) || 1);
    setLineas((prev) => {
      const line = prev[idx];
      if (!line || line.es_bonificacion) return prev;
      return recalcLineWithPromo(prev, idx, cantidad);
    });
  };

  // Inline price override per cart line. Marks the line with
  // `precio_override: true` so the backend skips the catalog re-pricing.
  // Recomputes subtotal locally so the running total stays in sync.
  const setLinePrice = (idx, price) => {
    const p = Math.max(0, parseFloat(price) || 0);
    setLineas((prev) => prev.map((l, i) =>
      i === idx && !l.es_bonificacion
        ? { ...l, precio_unitario: p, precio_override: true, subtotal: +(p * (l.cantidad || 0)).toFixed(2) }
        : l,
    ));
  };

  const openCreateCust = () => {
    setNewCust({
      ...newCustEmpty,
      nombre_facturacion: clienteTrimmed || "",
    });
    setNewCustOpen(true);
  };

  const submitCreateCust = async (e) => {
    e?.preventDefault();
    if (!newCust.nombre_facturacion.trim()) {
      toast.error("Ingresá un nombre");
      return;
    }
    setCreatingCust(true);
    try {
      const { data } = await api.post("/seller/customers", newCust);
      // refresh and auto-select
      setCustomers((prev) => [data, ...prev]);
      setCliente(data.nombre_facturacion);
      setClienteSel(data);
      setNewCustOpen(false);
      toast.success("Cliente creado y asignado a vos");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al crear cliente");
    } finally {
      setCreatingCust(false);
    }
  };

  const save = async () => {
    if (!cliente) { toast.error("Seleccioná un cliente"); return; }
    if (lineas.length === 0) { toast.error("Agregá al menos una línea"); return; }
    if (!clienteMatchesExisting || !clienteSel) {
      toast.error("Ese cliente no está en tu cartera. Tocá 'Crear nuevo cliente' primero.");
      return;
    }
    if (!(clienteSel.phone || "").trim()) {
      toast.error("El cliente no tiene teléfono. Completá los datos antes de crear el pedido.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/seller/orders", {
        nombre_cliente: cliente,
        cliente_id: clienteSel.id,
        // Backend re-applies discount/bonification rules from product catalog,
        // so we send only the non-bonus lines to avoid double-counting.
        // For lines with `precio_override`, the backend will honor the
        // submitted `precio_unitario` instead of re-pricing from the catalog.
        lineas: lineas
          .filter((l) => !l.es_bonificacion)
          .map(({ codigo, cantidad, precio_unitario, precio_override }) =>
            precio_override
              ? { codigo, cantidad, precio_unitario, precio_override: true }
              : { codigo, cantidad },
          ),
      });
      toast.success("Pedido guardado");
      setLastSavedOrder(data);
      setCliente("");
      setClienteSel(null);
      setLineas([]);
      clearDraft();
      setDraftRestored(false);
    } catch (err) {
      // Backend may return a structured "customer_incomplete" / "customer_required"
      // payload — surface the message verbatim.
      const det = err?.response?.data?.detail;
      if (det && typeof det === "object" && det.message) {
        toast.error(det.message);
      } else if (typeof det === "string") {
        toast.error(det);
      } else {
        toast.error("Error al guardar el pedido");
      }
    } finally {
      setSaving(false);
    }
  };

  const shareWhatsApp = (order) => {
    const lines = order.lineas
      .map((l) => `• ${l.codigo} x${l.cantidad} — ${formatMoney(l.subtotal)}`)
      .join("\n");
    const msg =
      `🛒 *Nuevo pedido*\n` +
      `Cliente: ${order.nombre_cliente}\n` +
      `Vendedor: ${order.nombre_vendedor}\n\n` +
      `${lines}\n\n` +
      `*Total: ${formatMoney(order.total)}*`;
    const url = `https://wa.me/?text=${encodeURIComponent(msg)}`;
    window.open(url, "_blank", "noopener");
  };

  if (loading) return <div className="text-center py-12 text-slate-500">Cargando…</div>;

  return (
    <div className="space-y-4 pb-24" data-testid="seller-pos-page">
      <h2 className="text-xl font-bold" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
        Tomar pedido
      </h2>

      {lastSavedOrder && (
        <Card className="border-emerald-200 bg-emerald-50/60 p-4 rounded-sm" data-testid="last-saved-card">
          <p className="text-sm font-semibold text-emerald-900">¡Pedido guardado!</p>
          <p className="text-xs text-emerald-800/80 mt-0.5">
            {lastSavedOrder.nombre_cliente} · {lastSavedOrder.lineas.length} líneas · {formatMoney(lastSavedOrder.total)}
          </p>
          <div className="flex gap-2 mt-3">
            <Button onClick={() => shareWhatsApp(lastSavedOrder)} className="rounded-sm bg-[#25D366] hover:bg-[#1ebe5d] text-white border-0" size="sm" data-testid="share-whatsapp-button">
              <MessageCircle className="h-4 w-4 mr-1.5" /> Compartir por WhatsApp
            </Button>
            <Button variant="outline" size="sm" onClick={() => setLastSavedOrder(null)} className="rounded-sm">
              Tomar otro
            </Button>
          </div>
        </Card>
      )}

      <Card className="bg-white border border-slate-200 rounded-sm p-4 space-y-4">
        <div>
          <label className="block text-sm font-medium mb-1.5">Cliente *</label>
          <Autocomplete
            items={customers}
            value={cliente}
            onChange={setCliente}
            onSelect={pickCustomer}
            searchKeys={["nombre_facturacion", "nombre_fantasia", "phone"]}
            placeholder={customers.length === 0 ? "Aún no tenés clientes — tocá '+ Cliente nuevo'" : "Buscá entre tus clientes…"}
            testid="seller-customer-input"
            inputClassName="rounded-sm"
            renderItem={(c) => (
              <div className="flex justify-between items-center gap-2">
                <span className="font-medium truncate">{c.nombre_facturacion}</span>
                {(c.nombre_fantasia || c.phone) && (
                  <span className="text-xs text-slate-500 truncate">{c.nombre_fantasia || c.phone}</span>
                )}
              </div>
            )}
          />
          {/* Inline "create new customer" affordance — appears when typed name
             does not match any existing assigned customer */}
          {(showCreateNewHint || customers.length === 0) && !clienteSel && (
            <button
              type="button"
              onClick={openCreateCust}
              className="mt-2 w-full flex items-center justify-between gap-2 px-3 py-2 border border-dashed border-[#002FA7] bg-[#EEF2FF] hover:bg-[#E0E7FF] rounded-sm transition-colors text-left"
              data-testid="seller-create-customer-inline"
            >
              <span className="flex items-center gap-2 text-sm text-[#002FA7] font-medium">
                <UserPlus className="h-4 w-4" />
                Crear nuevo cliente{clienteTrimmed ? `: "${clienteTrimmed}"` : ""}
              </span>
              <Plus className="h-4 w-4 text-[#002FA7]" />
            </button>
          )}

          {/* Customer snapshot card — appears once a real customer is picked
             from the autocomplete. Shows the data that will be saved with
             the order header (phone, address, fantasia). */}
          {clienteSel && (
            <Card
              className={`mt-2 border rounded-sm p-3 ${
                (clienteSel.phone || "").trim()
                  ? "border-emerald-200 bg-emerald-50/40"
                  : "border-amber-300 bg-amber-50"
              }`}
              data-testid="seller-selected-customer-card"
            >
              <div className="flex items-start gap-2">
                <div className="h-8 w-8 rounded-sm bg-white border border-emerald-200 flex items-center justify-center flex-shrink-0">
                  <UserPlus className="h-4 w-4 text-emerald-700" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="font-semibold text-slate-900 text-sm truncate">
                      {clienteSel.nombre_facturacion}
                    </span>
                    {clienteSel.nombre_fantasia && (
                      <span className="text-[10px] uppercase tracking-wider font-bold px-1 py-0.5 rounded-sm bg-white text-emerald-700 border border-emerald-200">
                        {clienteSel.nombre_fantasia}
                      </span>
                    )}
                  </div>
                  <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-slate-600">
                    {clienteSel.phone ? (
                      <a href={`tel:${clienteSel.phone}`} className="hover:text-[#002FA7] mono">
                        Tel: {clienteSel.phone}
                      </a>
                    ) : (
                      <span className="text-amber-700 font-bold italic" data-testid="seller-customer-no-phone">
                        FALTA TELÉFONO
                      </span>
                    )}
                    {clienteSel.cedula && <span className="mono">ID: {clienteSel.cedula}</span>}
                    {clienteSel.address && (
                      <span className="truncate max-w-[280px]">Dir: {clienteSel.address}</span>
                    )}
                  </div>
                  {clienteSel.notes && (
                    <p className="mt-1 text-[11px] text-slate-500 italic line-clamp-1">
                      {clienteSel.notes}
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => { setCliente(""); setClienteSel(null); }}
                  className="text-slate-400 hover:text-slate-700 p-1"
                  title="Cambiar cliente"
                  data-testid="seller-clear-customer-button"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
              {!(clienteSel.phone || "").trim() && (
                <div className="mt-2 pt-2 border-t border-amber-200 text-[11px] text-amber-800 font-medium" data-testid="seller-customer-incomplete-banner">
                  Este cliente no tiene teléfono. No podés crear pedidos sin completar este dato. Pedile al admin que lo actualice en el módulo de Clientes.
                </div>
              )}
              {/* Repeat last order — saves 30s for recurring customers */}
              <Button
                type="button"
                onClick={repeatLastOrder}
                disabled={repeating}
                variant="outline"
                size="sm"
                className="mt-2 w-full rounded-sm border-emerald-300 text-emerald-800 hover:bg-emerald-100"
                data-testid="seller-repeat-last-order"
              >
                {repeating ? <RefreshCw className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5 mr-1.5" />}
                Repetir último pedido de este cliente
              </Button>
            </Card>
          )}
        </div>

        {/* Product picker — shared POS grid (search + grid/list views) */}
        <POSProductGrid
          products={products}
          lineas={lineas}
          onAddProduct={quickAddFromCatalog}
          testid="seller-pos-grid"
          emptyHint="Sin resultados"
        />

        {/* Cart — shared POS cart with -, +, +1, +5, +10 + inline price edit */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium">Carrito del pedido</label>
            <span className="text-[11px] uppercase tracking-wide text-slate-500">
              {lineas.length} {lineas.length === 1 ? "línea" : "líneas"}
            </span>
          </div>
          <POSCart
            lineas={lineas}
            onChangeQty={setLineQty}
            onChangePrice={setLinePrice}
            onRemove={removeLine}
            testid="seller-pos-cart"
          />
        </div>
      </Card>

      {/* Sticky bottom action bar — always visible while seller scrolls
          through the catalog. Replaces the buried Save button so the
          seller can hit it with one tap from anywhere on the page. */}
      <div
        className="fixed bottom-0 left-0 right-0 z-30 bg-white border-t border-slate-200 shadow-[0_-4px_12px_rgba(0,0,0,0.05)] px-3 py-2 sm:px-6 sm:py-3 flex items-center gap-3"
        data-testid="pos-action-bar"
      >
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">
            {lineas.length} {lineas.length === 1 ? "línea" : "líneas"}{cliente ? ` · ${cliente.substring(0, 20)}` : ""}
          </div>
          <div className="text-lg sm:text-2xl font-black truncate" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
            {formatMoney(total)}
          </div>
        </div>
        <Button
          onClick={save}
          disabled={saving || lineas.length === 0 || !cliente || !clienteMatchesExisting}
          className="btn-primary rounded-sm h-11 sm:h-12 px-4 sm:px-6 text-sm sm:text-base shrink-0"
          data-testid="pos-save-order-button"
        >
          <Send className="h-4 w-4 mr-1.5" />
          {saving ? "Guardando…" : "Guardar"}
        </Button>
      </div>

      {/* New-customer dialog — full admin parity. Required is `nombre_facturacion`,
         everything else is optional but encouraged. Admin gets notified via
         the in-app bell + Activity feed whenever a seller creates a record. */}
      <Dialog open={newCustOpen} onOpenChange={setNewCustOpen}>
        <DialogContent className="max-w-2xl rounded-sm max-h-[90vh] overflow-y-auto" data-testid="seller-new-customer-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-[#002FA7]" />
              Nuevo cliente
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submitCreateCust} className="space-y-3 mt-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Razón social *</label>
                <Input
                  value={newCust.nombre_facturacion}
                  onChange={(e) => setNewCust({ ...newCust, nombre_facturacion: e.target.value })}
                  placeholder="Ej: Pulpería La Esquina S.A."
                  required
                  autoFocus
                  className="rounded-sm"
                  data-testid="seller-new-cust-name"
                />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Nombre de fantasía</label>
                <Input
                  value={newCust.nombre_fantasia}
                  onChange={(e) => setNewCust({ ...newCust, nombre_fantasia: e.target.value })}
                  placeholder="La Esquina"
                  className="rounded-sm"
                  data-testid="seller-new-cust-fantasia"
                />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Cédula / RUC</label>
                <Input
                  value={newCust.cedula}
                  onChange={(e) => setNewCust({ ...newCust, cedula: e.target.value })}
                  placeholder="3-101-000000"
                  className="rounded-sm mono"
                  data-testid="seller-new-cust-cedula"
                />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Teléfono principal</label>
                <Input
                  value={newCust.phone}
                  onChange={(e) => setNewCust({ ...newCust, phone: e.target.value })}
                  placeholder="8888-1234"
                  inputMode="tel"
                  className="rounded-sm"
                  data-testid="seller-new-cust-phone"
                />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Teléfono secundario</label>
                <Input
                  value={newCust.phone2}
                  onChange={(e) => setNewCust({ ...newCust, phone2: e.target.value })}
                  placeholder="8888-5678"
                  inputMode="tel"
                  className="rounded-sm"
                  data-testid="seller-new-cust-phone2"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Email</label>
                <Input
                  type="email"
                  value={newCust.email}
                  onChange={(e) => setNewCust({ ...newCust, email: e.target.value })}
                  placeholder="contacto@cliente.com"
                  className="rounded-sm"
                  data-testid="seller-new-cust-email"
                />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Provincia</label>
                <Input
                  value={newCust.provincia}
                  onChange={(e) => setNewCust({ ...newCust, provincia: e.target.value })}
                  placeholder="San José"
                  className="rounded-sm"
                  data-testid="seller-new-cust-provincia"
                />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Cantón</label>
                <Input
                  value={newCust.canton}
                  onChange={(e) => setNewCust({ ...newCust, canton: e.target.value })}
                  placeholder="Escazú"
                  className="rounded-sm"
                  data-testid="seller-new-cust-canton"
                />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Distrito</label>
                <Input
                  value={newCust.distrito}
                  onChange={(e) => setNewCust({ ...newCust, distrito: e.target.value })}
                  placeholder="San Rafael"
                  className="rounded-sm"
                  data-testid="seller-new-cust-distrito"
                />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Código postal</label>
                <Input
                  value={newCust.codigo_postal}
                  onChange={(e) => setNewCust({ ...newCust, codigo_postal: e.target.value })}
                  placeholder="10101"
                  className="rounded-sm mono"
                  data-testid="seller-new-cust-codigo-postal"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Dirección exacta</label>
                <Textarea
                  value={newCust.address}
                  onChange={(e) => setNewCust({ ...newCust, address: e.target.value })}
                  placeholder="200m sur del parque, casa color verde…"
                  rows={2}
                  className="rounded-sm"
                  data-testid="seller-new-cust-address"
                />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Categoría</label>
                <Input
                  value={newCust.category}
                  onChange={(e) => setNewCust({ ...newCust, category: e.target.value })}
                  placeholder="Pulpería / Restaurante / …"
                  className="rounded-sm"
                  data-testid="seller-new-cust-category"
                />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Actividad económica</label>
                <Input
                  value={newCust.actividad_economica}
                  onChange={(e) => setNewCust({ ...newCust, actividad_economica: e.target.value })}
                  placeholder="Venta minorista de abarrotes"
                  className="rounded-sm"
                  data-testid="seller-new-cust-actividad"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Link de Google Maps</label>
                <Input
                  type="url"
                  value={newCust.maps_link}
                  onChange={(e) => setNewCust({ ...newCust, maps_link: e.target.value })}
                  placeholder="https://maps.app.goo.gl/…"
                  className="rounded-sm"
                  data-testid="seller-new-cust-maps"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Notas</label>
                <Textarea
                  value={newCust.notes}
                  onChange={(e) => setNewCust({ ...newCust, notes: e.target.value })}
                  placeholder="Horario, contacto, días de entrega, etc."
                  rows={2}
                  className="rounded-sm"
                  data-testid="seller-new-cust-notes"
                />
              </div>
            </div>
            <p className="text-[11px] text-slate-500 italic flex items-center gap-1.5">
              <UserPlus className="h-3 w-3" />
              El cliente quedará asignado a vos y tu administrador recibirá una notificación.
            </p>
            <div className="flex justify-end gap-2 pt-2 sticky bottom-0 bg-white">
              <Button type="button" variant="outline" onClick={() => setNewCustOpen(false)} className="rounded-sm">
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={creatingCust}
                className="btn-primary rounded-sm"
                data-testid="seller-new-cust-submit"
              >
                {creatingCust ? "Guardando…" : "Crear cliente"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
